import {
  closestCenter,
  DndContext,
  DragEndEvent,
  DragOverEvent,
  KeyboardSensor,
  MouseSensor,
  TouchSensor,
  useDndContext,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  ArrowUturnLeftIcon,
  Cog6ToothIcon,
  EyeIcon,
  EyeSlashIcon,
} from '@heroicons/react/24/outline';
import { Button } from '@main/complykit/components/button';
import { Icon } from '@main/complykit/components/icon';
import { IconButton } from '@main/complykit/components/icon-button';
import { Link } from '@main/complykit/components/link';
import { Popover } from '@main/complykit/components/popover';
import { SearchField } from '@main/complykit/components/search-field';
import { Content, Footer } from '@main/complykit/components/slots';
import { Stack } from '@main/complykit/components/stack';
import { Text } from '@main/complykit/components/text';
import { Column, Table, VisibilityState } from '@tanstack/react-table';
import {
  FC,
  PropsWithChildren,
  useDeferredValue,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';

import { DragHandleGlyph } from '../../../icons/drag-handle';
import { OverflowContainer } from '../../overflow-tooltip/overflow-tooltip';
import {
  orderColumnsByColumnOrder,
  useGetTableSettingsStateFromTable,
} from '../table-settings-state';

/* The row and its handle are dnd-kit's: the ref, the transform and the
 * handle's prop bag land on plain elements (escape-hatches.md). */
const SortableColumn: FC<PropsWithChildren<{ id: string; handleLabel: string }>> = ({
  id,
  handleLabel,
  children,
}) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id,
  });

  const style = useMemo(
    () => ({
      transform: `translate3d(${transform?.x ?? 0}px, ${transform?.y ?? 0}px, 0)`,
      transition,
    }),
    [transform, transition],
  );

  return (
    <li ref={setNodeRef} style={style}>
      <Stack direction="row" gap={2} align="center">
        <span
          {...attributes}
          {...listeners}
          aria-label={handleLabel}
          style={{ display: 'flex', cursor: isDragging ? 'grabbing' : 'grab' }}
        >
          <Icon as={DragHandleGlyph} size="sm" color="muted" />
        </span>
        {children}
      </Stack>
    </li>
  );
};

const SortableHeader: FC<PropsWithChildren<{ id: string }>> = ({ id, children }) => {
  const { setNodeRef, transform, transition } = useSortable({
    id,
  });

  const style = useMemo(
    () => ({
      transform: CSS.Transform.toString(transform),
      transition,
    }),
    [transform, transition],
  );

  return (
    <div ref={setNodeRef} style={style}>
      {children}
    </div>
  );
};

/* dnd-kit hears Escape on the document, after the popover and the drawer
 * around it have already taken the key as a close. While a drag is live the
 * key is caught first and handed to dnd-kit alone, so it cancels the drag.
 * Rendered inside the DndContext, whose active item says whether one is live. */
const EscapeCancelsDrag = () => {
  const isDragging = useDndContext().active !== null;
  useEffect(() => {
    if (!isDragging) return;
    const forwarded = new WeakSet<Event>();
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape' || forwarded.has(event)) return;
      event.stopPropagation();
      event.preventDefault();
      const cancel = new KeyboardEvent('keydown', { key: 'Escape', code: 'Escape' });
      forwarded.add(cancel);
      document.dispatchEvent(cancel);
    };
    document.addEventListener('keydown', onKeyDown, true);
    return () => document.removeEventListener('keydown', onKeyDown, true);
  }, [isDragging]);
  return null;
};

const isEqual = (a: unknown, b: unknown): boolean => {
  if (a === b) return true;

  if (typeof a !== typeof b) return false;

  if (Array.isArray(a) && Array.isArray(b)) {
    if (a.length !== b.length) return false;
    return a.every((item, index) => isEqual(item, b[index]));
  }

  if (a && b && typeof a === 'object' && typeof b === 'object') {
    const aKeys = Object.keys(a);
    const bKeys = Object.keys(b);

    if (aKeys.length !== bKeys.length) return false;

    const allMatch = aKeys.every((key) =>
      isEqual((a as Record<string, unknown>)[key], (b as Record<string, unknown>)[key]),
    );
    return allMatch;
  }

  return false;
};

const useTableSettingsState = <TData,>(table: Table<TData>) => {
  const stateFromTable = useGetTableSettingsStateFromTable(table);
  const prevStateFromTable = useDeferredValue(stateFromTable);

  const [columnVisibility, setColumnVisibility] = useState(stateFromTable.columnVisibility);
  const [columnOrder, setColumnOrder] = useState(() =>
    stateFromTable.columnOrder.toSorted(sortByVisible(stateFromTable.columnVisibility.hidden)),
  );

  useEffect(() => {
    if (
      !isEqual(prevStateFromTable.columnVisibility, stateFromTable.columnVisibility) ||
      !isEqual(prevStateFromTable.columnOrder, stateFromTable.columnOrder)
    ) {
      setColumnVisibility(stateFromTable.columnVisibility);
      setColumnOrder(
        stateFromTable.columnOrder.toSorted(sortByVisible(stateFromTable.columnVisibility.hidden)),
      );
    }
  }, [prevStateFromTable.columnOrder, prevStateFromTable.columnVisibility, stateFromTable]);

  const visibleColumnOrder = useMemo(
    () => columnOrder.filter((id) => columnVisibility.visible.some((column) => column.id === id)),
    [columnOrder, columnVisibility.visible],
  );
  const hiddenColumnOrder = useMemo(
    () => columnOrder.filter((id) => columnVisibility.hidden.some((column) => column.id === id)),
    [columnOrder, columnVisibility.hidden],
  );

  const columnOrderWithHeader = useMemo(
    () => [...visibleColumnOrder, HIDDEN_COLUMNS_HEADER_ID, ...hiddenColumnOrder],
    [hiddenColumnOrder, visibleColumnOrder],
  );

  return {
    stateFromTable,
    columns: stateFromTable.columns,
    columnOrder,
    columnOrderWithHeader,
    setColumnOrder,
    columnVisibility,
    setColumnVisibility,
  };
};

const HIDDEN_COLUMNS_HEADER_ID = 'HIDDEN_COLUMNS_HEADER';

export function TableSettings<TData>({
  table,
  hasColumnOrderChanged,
  hasColumnVisibilityChanged,
  hasColumnSizingChanged,
}: {
  table: Table<TData>;
  hasColumnOrderChanged: boolean;
  hasColumnVisibilityChanged: boolean;
  hasColumnSizingChanged: boolean;
}) {
  const { t } = useTranslation('ui');
  const {
    stateFromTable,
    columns,
    columnOrder,
    columnOrderWithHeader,
    setColumnOrder,
    columnVisibility,
    setColumnVisibility,
  } = useTableSettingsState(table);

  const popoverRef = useRef<HTMLDivElement>(null);
  /* Hiding, showing or resetting removes the control that had focus; left to
   * React Aria, focus lands on the close control, one Enter from closing the
   * panel. It stays on the panel instead. */
  const focusPanel = () =>
    popoverRef.current?.querySelector<HTMLElement>('[role="dialog"]')?.focus();

  const hideColumn = (column: Column<TData>) => {
    column.toggleVisibility(false);
    const newHidden = [...columnVisibility.hidden, column];
    const newColumnOrder = columnOrder.toSorted(sortByVisible(newHidden));
    table.setColumnOrder(newColumnOrder);
    focusPanel();
  };
  const showColumn = (column: Column<TData>) => {
    column.toggleVisibility(true);
    const newHidden = columnVisibility.hidden.filter((c) => c.id !== column.id);
    const newColumnOrder = columnOrder.toSorted(sortByVisible(newHidden));
    table.setColumnOrder(newColumnOrder);
    focusPanel();
  };
  const hideAll = () => {
    table.setColumnVisibility((state) =>
      columns.reduce(
        (prev, column) =>
          ({
            ...prev,
            [column.id]: column.getCanHide() ? false : prev[column.id],
          }) as VisibilityState,
        state,
      ),
    );
    focusPanel();
  };
  const showAll = () => {
    table.setColumnVisibility((state) =>
      columns.reduce(
        (prev, column) => ({
          ...prev,
          [column.id]: true,
        }),
        state,
      ),
    );
    focusPanel();
  };

  const dndSensors = useSensors(
    useSensor(MouseSensor),
    useSensor(TouchSensor),
    useSensor(KeyboardSensor),
  );

  const [search, setSearch] = useState('');

  const showResetConfiguration = hasColumnOrderChanged || hasColumnVisibilityChanged;
  const orderedVisibleColumns = useMemo(
    () => orderColumnsByColumnOrder(columnVisibility.visible, columnOrder),
    [columnOrder, columnVisibility.visible],
  );
  const orderedHiddenColumns = useMemo(
    () => orderColumnsByColumnOrder(columnVisibility.hidden, columnOrder),
    [columnOrder, columnVisibility.hidden],
  );

  const label = (name: string) => (
    <OverflowContainer>
      <OverflowContainer.Tooltip label={name}>
        <Text size="sm" isTruncated>
          {name}
        </Text>
      </OverflowContainer.Tooltip>
    </OverflowContainer>
  );

  const columnRow = (column: Column<TData>, isVisible: boolean) => {
    const columnName = column.columnDef.meta?.name || '';
    return (
      <SortableColumn
        key={column.id}
        id={column.id}
        handleLabel={t('table.settings.reorderColumn', { columnName })}
      >
        {label(columnName)}
        {isVisible ? (
          <IconButton
            icon={EyeIcon}
            isDisabled={!column.getCanHide()}
            label={t('table.settings.hideColumn', { columnName })}
            onPress={() => hideColumn(column)}
          />
        ) : (
          <IconButton
            icon={EyeSlashIcon}
            label={t('table.settings.showColumn', { columnName })}
            onPress={() => showColumn(column)}
          />
        )}
      </SortableColumn>
    );
  };

  return (
    <Popover
      ref={popoverRef}
      placement="bottom-end"
      trigger={
        <IconButton
          variant="outline"
          icon={Cog6ToothIcon}
          label={t('table.settings.buttonLabel')}
        />
      }
      title={t('table.settings.tableConfigurationHeading')}
      closeLabel={t('table.settings.close')}
    >
      <Content>
        <SearchField
          aria-label={t('table.settings.searchLabel')}
          placeholder={t('table.settings.searchPlaceholder')}
          clearLabel={t('table.settings.clearSearch')}
          value={search}
          onChange={setSearch}
        />
      </Content>
      <Content isScrollable maxHeight="50vh">
        <DndContext
          collisionDetection={closestCenter}
          modifiers={[restrictToVerticalAxis]}
          onDragEnd={handleDragEnd}
          onDragOver={handleDragOver}
          sensors={dndSensors}
          autoScroll
        >
          <EscapeCancelsDrag />
          <SortableContext items={columnOrderWithHeader} strategy={verticalListSortingStrategy}>
            <Stack gap={4}>
              {stateFromTable.columnVisibility.visible.length > 0 && (
                <Stack gap={1}>
                  <Stack direction="row" justify="between">
                    <Text size="xs" weight="medium" color="muted">
                      {t('table.settings.shownInTable')}
                    </Text>
                    {/* The xs step rides Text: Link's own sizes stop at sm. */}
                    <Text as="span" size="xs" weight="medium">
                      <Link onPress={hideAll}>{t('table.settings.hideAll')}</Link>
                    </Text>
                  </Stack>
                  <Stack as="ul" gap={1}>
                    {orderedVisibleColumns
                      .filter(filterColumns(search))
                      .map((column) => columnRow(column, true))}
                  </Stack>
                </Stack>
              )}

              {stateFromTable.columnVisibility.hidden.length > 0 && (
                <Stack gap={1}>
                  <SortableHeader id={HIDDEN_COLUMNS_HEADER_ID}>
                    <Stack direction="row" justify="between">
                      <Text size="xs" weight="medium" color="muted">
                        {t('table.settings.hiddenInTable')}
                      </Text>
                      <Text as="span" size="xs" weight="medium">
                        <Link onPress={showAll}>{t('table.settings.showAll')}</Link>
                      </Text>
                    </Stack>
                  </SortableHeader>
                  <Stack as="ul" gap={1}>
                    {orderedHiddenColumns
                      .filter(filterColumns(search))
                      .map((column) => columnRow(column, false))}
                  </Stack>
                </Stack>
              )}
            </Stack>
          </SortableContext>
        </DndContext>
      </Content>
      {(showResetConfiguration || hasColumnSizingChanged) && (
        <Footer isDivided>
          <Stack gap={0} align="start">
            {showResetConfiguration && (
              <Button
                variant="ghost"
                iconStart={ArrowUturnLeftIcon}
                onPress={() => {
                  table.resetColumnVisibility();
                  table.resetColumnOrder();
                  focusPanel();
                }}
              >
                {t('table.settings.resetTableConfiguration')}
              </Button>
            )}
            {hasColumnSizingChanged && (
              <Button
                variant="ghost"
                iconStart={ArrowUturnLeftIcon}
                onPress={() => {
                  table.resetColumnSizing();
                  focusPanel();
                }}
              >
                {t('table.settings.resetColumnWidth')}
              </Button>
            )}
          </Stack>
        </Footer>
      )}
    </Popover>
  );

  function handleDragOver(event: DragOverEvent) {
    setColumnVisibility((state) => {
      const currentlyVisible = state.visible.some(({ id }) => id === event.active.id);
      const targetVisible =
        event.over?.id === HIDDEN_COLUMNS_HEADER_ID
          ? !currentlyVisible
          : state.visible.some(({ id }) => id === event.over?.id);
      const column = columns.find(({ id }) => id === event.active.id);
      if (
        column &&
        currentlyVisible !== targetVisible &&
        (targetVisible === true || column.getCanHide())
      ) {
        if (targetVisible) {
          return {
            visible: [...state.visible, column],
            hidden: state.hidden.filter((c) => c.id !== column.id),
          };
        } else {
          return {
            visible: state.visible.filter((c) => c.id !== column.id),
            hidden: [...state.hidden, column],
          };
        }
      }
      return state;
    });
  }

  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event;

    if (over?.id && active.id !== over.id) {
      setColumnOrder((items) => {
        const oldIndex = items.indexOf(active.id.toString());
        const newIndex = items.indexOf(over.id.toString());
        const newColumnOrder = arrayMove(items, oldIndex, newIndex);
        const column = columns.find(({ id }) => id === active.id);
        const oldIsVisible = column?.getIsVisible();
        const newIsVisible =
          over.id === HIDDEN_COLUMNS_HEADER_ID
            ? !oldIsVisible
            : columnVisibility.visible.some(({ id }) => id === active.id) ||
              newIndex < columnVisibility.visible.length;
        if (oldIsVisible !== newIsVisible) {
          if (newIsVisible === false && !column?.getCanHide()) {
            return items;
          }
          table.setColumnVisibility((state) => ({
            ...state,
            [active.id]: newIsVisible,
          }));
        }
        table.setColumnOrder(newColumnOrder);
        return newColumnOrder;
      });
    } else if (over?.id && active.id === over.id) {
      // This happens when an item is moved over the threshold.
      // Local state visibility should be correct, so just propagate
      // the change to the table.
      table.setColumnVisibility({
        ...columnVisibility.visible.reduce(
          (prev, column) => ({
            ...prev,
            [column.id]: true,
          }),
          {},
        ),
        ...columnVisibility.hidden.reduce(
          (prev, column) => ({
            ...prev,
            [column.id]: false,
          }),
          {},
        ),
      });
    }
    focusPanel();
  }
}

function sortByVisible(hidden: { id: string }[]) {
  return (a: string, b: string) => {
    const isVisibleA = !hidden.some((column) => column.id === a);
    const isVisibleB = !hidden.some((column) => column.id === b);

    // If both are visible or both are hidden, maintain original order
    if (isVisibleA === isVisibleB) return 0;

    // Prioritize visible columns
    return isVisibleA ? -1 : 1;
  };
}

function filterColumns<TData>(search: string) {
  return (column: Column<TData>) =>
    column.columnDef.meta?.name?.toLowerCase().includes(search.toLowerCase());
}
